(() => {
  if (window.__hanfiShoppableVideoReady) return;
  window.__hanfiShoppableVideoReady = true;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const idle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 1));

  const loadVideo = (host, shouldPlay = false) => {
    if (!host || host.dataset.videoLoaded === 'true') {
      const existing = host?.querySelector('video');
      if (shouldPlay && existing && !prefersReducedMotion()) existing.play().catch(() => {});
      return;
    }

    const template = host.querySelector('template[data-hanfi-video-template]');
    const media = host.matches('[data-hanfi-video-media]') ? host : host.querySelector('[data-hanfi-video-media]') || host;
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (video) {
      video.preload = host.dataset.preload || 'none';
      video.muted = true;
      video.playsInline = true;
      video.controls = host.dataset.controls === 'true';
      if (host.dataset.loop !== 'false') video.loop = true;
    }

    media.appendChild(fragment);
    host.dataset.videoLoaded = 'true';
    host.classList.add('is-video-loaded');

    if (video && (shouldPlay || host.dataset.autoplay === 'true') && !prefersReducedMotion()) {
      video.play().catch(() => {});
    }
  };

  const pauseInactiveVideos = (root, activePanel) => {
    root.querySelectorAll('video').forEach((video) => {
      if (activePanel && activePanel.contains(video)) return;
      video.pause();
    });
  };

  const resetOtherAudioToggles = (button) => {
    document.querySelectorAll('[data-hanfi-video-audio-toggle][aria-pressed="true"]').forEach((activeButton) => {
      if (activeButton === button) return;
      activeButton.setAttribute('aria-pressed', 'false');
      activeButton.setAttribute('aria-label', activeButton.closest('[data-hanfi-video-panel]') ? 'Unmute video' : 'Unmute preview');
    });
  };

  const unmuteVideo = (video, button) => {
    if (!video) return;
    document.querySelectorAll('video').forEach((otherVideo) => {
      if (otherVideo !== video) otherVideo.muted = true;
    });
    resetOtherAudioToggles(button);
    video.muted = false;
    video.volume = 1;
    if (button) {
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', button.closest('[data-hanfi-video-panel]') ? 'Mute video' : 'Mute preview');
    }
  };

  const updateModalCartButton = (root, activePanel) => {
    const button = root.querySelector('[data-hanfi-modal-cart-submit]');
    if (!button) return;

    const hasProductForm = Boolean(activePanel?.querySelector('[data-hanfi-add-form], .hanfi-shoppable-modal__form, .hanfi-floating-video__form'));
    button.hidden = !hasProductForm;
    button.disabled = !hasProductForm;
    button.setAttribute('aria-hidden', hasProductForm ? 'false' : 'true');
  };

  const activatePanel = (root, index) => {
    const panels = Array.from(root.querySelectorAll('[data-hanfi-video-panel]'));
    if (!panels.length) return null;
    const nextIndex = (Number(index) + panels.length) % panels.length;
    root.dataset.activeIndex = String(nextIndex);

    panels.forEach((panel, panelIndex) => {
      const active = panelIndex === nextIndex;
      panel.hidden = !active;
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) {
        const host = panel.querySelector('[data-hanfi-video-host]');
        loadVideo(host, true);
      }
    });

    const activePanel = panels[nextIndex];
    pauseInactiveVideos(root, activePanel);
    updateModalCartButton(root, activePanel);
    return activePanel;
  };

  const normalizeQuantity = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const hydratePdpControls = (target) => {
    if (!target || target.dataset.hanfiPdpHydrated === 'true') return;
    target.dataset.hanfiPdpHydrated = 'true';

    const variantData = target.querySelector('[data-hanfi-variant-data]');
    const variants = variantData ? JSON.parse(variantData.textContent || '[]') : [];
    const optionGroups = Array.from(target.querySelectorAll('[data-hanfi-option-group]'));
    const variantInputs = Array.from(target.querySelectorAll('[data-hanfi-variant-id-input]'));
    const priceEls = Array.from(target.querySelectorAll('[data-hanfi-price]'));
    const compareEls = Array.from(target.querySelectorAll('[data-hanfi-compare-price]'));
    const discountEls = Array.from(target.querySelectorAll('[data-hanfi-discount]'));
    const atcButtons = Array.from(target.querySelectorAll('[data-hanfi-atc-button], [data-hanfi-buy-now]'));
    const addForm = target.querySelector('[data-hanfi-add-form]');
    const quantityInputs = Array.from(target.querySelectorAll('[data-hanfi-quantity-input]'));
    const lineQuantityInputs = Array.from(target.querySelectorAll('[data-hanfi-line-quantity]'));
    const variantInfoRows = Array.from(target.querySelectorAll('[data-hanfi-info-row][data-hanfi-info-source="variant"]'));
    const infoCards = Array.from(target.querySelectorAll('[data-hanfi-info-card]'));
    let selectedQuantity = 1;
    let activeVariant = variants.find((variant) => String(variant.id) === String(variantInputs[0]?.value)) || variants[0];

    target.querySelectorAll('[id]').forEach((element) => {
      element.id = `${element.id}-modal-${Math.random().toString(36).slice(2, 8)}`;
    });

    const selectedOptions = () => optionGroups.map((group) => {
      const checked = group.querySelector('input:checked');
      return checked ? checked.value : '';
    });

    const findVariant = (options) => variants.find((variant) => (
      Array.isArray(variant.options) && variant.options.every((option, index) => option === options[index])
    ));

    const setQuantity = (value) => {
      selectedQuantity = normalizeQuantity(value);
      quantityInputs.forEach((input) => { input.value = selectedQuantity; });
      lineQuantityInputs.forEach((input) => { input.value = selectedQuantity; });
    };

    const updateInfoCardVisibility = () => {
      infoCards.forEach((card) => {
        const hasVisibleRows = Array.from(card.querySelectorAll('.hanfi-info-item')).some((row) => !row.hidden);
        card.hidden = !hasVisibleRows;
      });
    };

    const updateVariantInfoRows = (variant) => {
      const metafields = variant?.metafields || {};
      variantInfoRows.forEach((row) => {
        const value = row.querySelector('[data-hanfi-info-value]');
        const html = metafields[row.dataset.hanfiInfoKey] || '';
        row.hidden = !html;
        if (value) value.innerHTML = html;
      });
      updateInfoCardVisibility();
    };

    const setButtonState = (available) => {
      atcButtons.forEach((button) => {
        button.disabled = !available;
        button.classList.toggle('is-disabled', !available);
        if (button.matches('[data-hanfi-atc-button]')) {
          button.textContent = available ? button.dataset.defaultText : button.dataset.soldOutText;
        }
      });
    };

    const updateChoiceAvailability = () => {
      if (!optionGroups.length || !variants.length) return;
      const currentOptions = selectedOptions();
      optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('input').forEach((input) => {
          const testOptions = [...currentOptions];
          testOptions[groupIndex] = input.value;
          const possible = variants.some((variant) => (
            variant.available && variant.options.every((option, index) => option === testOptions[index])
          ));
          input.closest('.hanfi-variant-choice')?.classList.toggle('is-disabled', !possible);
          input.toggleAttribute('aria-disabled', !possible);
        });
      });
    };

    const updateVariantUI = (variant) => {
      if (!variant) {
        setButtonState(false);
        return;
      }

      activeVariant = variant;
      variantInputs.forEach((input) => { input.value = variant.id; });
      priceEls.forEach((element) => { element.textContent = variant.price_formatted; });
      updateVariantInfoRows(variant);

      const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;
      compareEls.forEach((element) => {
        element.hidden = !hasComparePrice;
        if (hasComparePrice) element.textContent = variant.compare_at_price_formatted;
      });
      discountEls.forEach((element) => {
        element.hidden = !hasComparePrice;
        if (hasComparePrice) {
          element.textContent = `${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)}% OFF`;
        }
      });

      optionGroups.forEach((group) => {
        const selected = group.querySelector('input:checked');
        const label = group.querySelector('[data-hanfi-selected-value]');
        if (selected && label) label.textContent = selected.value;
      });

      updateChoiceAvailability();
      setButtonState(Boolean(variant.available));
    };

    target.querySelectorAll('.hanfi-pill-group').forEach((group) => {
      group.querySelectorAll('.hanfi-pill').forEach((button) => {
        button.addEventListener('click', () => {
          group.querySelectorAll('.hanfi-pill').forEach((pill) => pill.classList.remove('active'));
          button.classList.add('active');
        });
      });
    });

    optionGroups.forEach((group) => {
      group.addEventListener('click', (event) => {
        const input = event.target.closest('input');
        if (input?.getAttribute('aria-disabled') === 'true') event.preventDefault();
      });
      group.addEventListener('change', () => updateVariantUI(findVariant(selectedOptions())));
    });

    target.querySelectorAll('[data-hanfi-buy-now]').forEach((button) => {
      button.addEventListener('click', () => {
        if (activeVariant?.available) window.location.href = `/cart/${activeVariant.id}:${selectedQuantity}`;
      });
    });

    quantityInputs.forEach((input) => {
      input.addEventListener('input', () => setQuantity(input.value));
      input.addEventListener('blur', () => setQuantity(input.value));
    });
    target.querySelectorAll('[data-hanfi-qty-minus]').forEach((button) => {
      button.addEventListener('click', () => setQuantity(selectedQuantity - 1));
    });
    target.querySelectorAll('[data-hanfi-qty-plus]').forEach((button) => {
      button.addEventListener('click', () => setQuantity(selectedQuantity + 1));
    });

    addForm?.addEventListener('submit', () => {
      lineQuantityInputs.forEach((input) => { input.value = selectedQuantity; });
    });

    target.querySelectorAll('[data-hanfi-size-chart]').forEach((sizeChart) => {
      const dialog = sizeChart.querySelector('[data-hanfi-size-chart-dialog]');
      const close = sizeChart.querySelector('[data-hanfi-size-chart-close]');
      if (!dialog) return;
      target.querySelectorAll('[data-hanfi-size-chart-open]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
          else dialog.setAttribute('open', '');
        });
      });
      close?.addEventListener('click', () => dialog.close?.());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close?.();
      });
    });

    setQuantity(selectedQuantity);
    updateVariantUI(activeVariant);
  };

  const usefulPdpSelector = [
    '.hanfi-more-options',
    '[data-hanfi-variant-picker]',
    '[data-hanfi-quantity]',
    '[data-hanfi-info-card]',
    '.hanfi-info-grid',
    '.hanfi-trust-row',
    '[data-hanfi-size-chart]',
    '.hanfi-pill-group'
  ].join(', ');

  const hasPurchaseControls = (element) => Boolean(element?.querySelector(
    '[data-hanfi-buy-wrapper], [data-hanfi-atc-button], [data-hanfi-buy-now], [data-hanfi-add-form]'
  ));

  const collectPdpBlocks = (productInfo) => {
    const blocks = [];
    if (!productInfo) return blocks;

    Array.from(productInfo.children).forEach((child) => {
      if (child.matches(usefulPdpSelector) || child.querySelector(usefulPdpSelector)) {
        blocks.push(child.cloneNode(true));
      }
    });

    return blocks;
  };

  const loadPdpDetails = async (panel) => {
    const target = panel?.querySelector('[data-hanfi-pdp-detail-target]');
    if (!target || target.dataset.hanfiPdpLoaded === 'true' || target.dataset.hanfiPdpLoading === 'true') return;
    const productUrl = target.dataset.productUrl;
    if (!productUrl) return;

    target.dataset.hanfiPdpLoading = 'true';
    const fallback = target.querySelector('[data-hanfi-pdp-fallback]');
    const slot = target.querySelector('[data-hanfi-pdp-slot]') || document.createElement('div');
    if (!slot.dataset.hanfiPdpSlot) {
      slot.className = 'hanfi-shoppable-modal__pdp-slot';
      slot.dataset.hanfiPdpSlot = '';
      target.append(slot);
    }
    const loading = document.createElement('div');
    loading.className = 'hanfi-shoppable-pdp-loading';
    loading.textContent = 'Loading product options';
    slot.append(loading);

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('section_id', target.dataset.pdpSectionId || 'main');
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error('PDP section fetch failed');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const productInfo = doc.querySelector('.hanfi-product-info');
      const variantData = doc.querySelector('[data-hanfi-variant-data]');
      if (!productInfo) throw new Error('PDP product info missing');

      const shell = document.createElement('div');
      shell.className = 'hanfi-shoppable-pdp-detail';
      const blocks = collectPdpBlocks(productInfo);
      if (blocks.length) {
        shell.append(...blocks);
      } else {
        shell.append(...Array.from(productInfo.children).map((child) => child.cloneNode(true)));
      }

      slot.replaceChildren(shell);
      if (variantData) slot.append(variantData.cloneNode(true));
      hydratePdpControls(target);
      if (fallback && hasPurchaseControls(shell)) target.classList.add('has-pdp-controls');
      target.dataset.hanfiPdpLoaded = 'true';
    } catch (_error) {
      loading.remove();
      hydratePdpControls(target);
      target.dataset.hanfiPdpFailed = 'true';
    } finally {
      target.dataset.hanfiPdpLoading = 'false';
    }
  };

  const openModal = (root, index) => {
    const dialog = root.querySelector('dialog[data-hanfi-video-modal]');
    if (!dialog) return;
    const activePanel = activatePanel(root, index);
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    const activeHost = activePanel?.querySelector('[data-hanfi-video-host]');
    const activeAudioButton = activePanel?.querySelector('[data-hanfi-video-audio-toggle]');
    if (activeHost) loadVideo(activeHost, false);
    const activeVideo = activeHost?.querySelector('video') || activePanel?.querySelector('video');
    if (activeVideo && !prefersReducedMotion()) {
      unmuteVideo(activeVideo, activeAudioButton);
      window.requestAnimationFrame(() => activeVideo.play().catch(() => {}));
    }
    loadPdpDetails(activePanel);
  };

  const closeModal = (root) => {
    const dialog = root.querySelector('dialog[data-hanfi-video-modal]');
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    pauseInactiveVideos(root, null);
  };

  const setupLazyHosts = (root) => {
    const visibleHosts = Array.from(root.querySelectorAll('[data-hanfi-video-host]')).filter((host) => {
      if (host.closest('dialog')) return false;
      return host.dataset.loadMode === 'visible' || host.dataset.autoplay === 'true';
    });
    root.querySelectorAll('[data-hanfi-video-host]').forEach((host) => {
      ['pointerenter', 'focusin'].forEach((eventName) => {
        host.addEventListener(eventName, () => loadVideo(host, host.dataset.autoplay === 'true'), { once: true });
      });
    });

    if (!visibleHosts.length) return;
    if (!('IntersectionObserver' in window)) {
      visibleHosts.forEach((host) => loadVideo(host, host.dataset.autoplay === 'true'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        idle(() => loadVideo(entry.target, entry.target.dataset.autoplay === 'true'));
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '160px 0px', threshold: 0.01 });

    visibleHosts.forEach((host) => observer.observe(host));
  };

  const setupInlineSection = (root) => {
    const track = root.querySelector('[data-hanfi-video-track]');
    const prev = root.querySelector('[data-hanfi-video-prev]');
    const next = root.querySelector('[data-hanfi-video-next]');
    const dotsHost = root.querySelector('[data-hanfi-video-dots]');

    if (track) {
      const isOverflowing = () => track.scrollWidth > track.clientWidth + 2;
      const pageCount = () => isOverflowing() ? Math.max(1, Math.ceil(track.scrollWidth / Math.max(1, track.clientWidth))) : 1;
      const activePage = () => Math.min(pageCount() - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));

      const updateControls = () => {
        const overflowing = isOverflowing();
        const atStart = !overflowing || track.scrollLeft <= 2;
        const atEnd = !overflowing || track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        [[prev, atStart], [next, atEnd]].forEach(([button, disabled]) => {
          if (!button) return;
          button.disabled = disabled;
          button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        });
        if (!dotsHost) return;
        Array.from(dotsHost.children).forEach((dot, index) => dot.classList.toggle('is-active', index === activePage()));
      };

      const renderDots = () => {
        if (!dotsHost) {
          updateControls();
          return;
        }
        dotsHost.innerHTML = '';
        if (isOverflowing()) {
          for (let index = 0; index < pageCount(); index += 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.setAttribute('aria-label', `Go to video group ${index + 1}`);
            dot.addEventListener('click', () => track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' }));
            dotsHost.appendChild(dot);
          }
        }
        updateControls();
      };

      prev?.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: 'smooth' }));
      track.addEventListener('scroll', () => window.requestAnimationFrame(updateControls), { passive: true });
      window.addEventListener('resize', () => window.requestAnimationFrame(renderDots), { passive: true });
      renderDots();
    }

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-hanfi-video-open]');
      if (!trigger || !root.contains(trigger)) return;
      event.preventDefault();
      openModal(root, trigger.dataset.videoIndex || 0);
    });
  };

  const setupFloatingSection = (root) => {
    const storageKey = `hanfi-floating-video:${root.id}`;
    const persistClose = root.dataset.persistClose === 'true';
    const initialDelay = Math.max(0, Number(root.dataset.loadDelay) || 0);
    const mobileDefaultPreview = root.dataset.mobileDefaultPreview || 'expanded';
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    let previewStateTouched = false;

    if (persistClose && sessionStorage.getItem(storageKey) === 'closed') {
      root.dataset.floatState = 'closed';
    }

    const applyResponsivePreviewDefault = () => {
      if (previewStateTouched || root.dataset.floatState === 'closed') return;
      root.dataset.floatState = mobileDefaultPreview === 'circle' && mobileQuery.matches ? 'minimized' : 'expanded';
    };

    applyResponsivePreviewDefault();

    const expandedHost = root.querySelector('[data-hanfi-float-expanded] [data-hanfi-video-host]');
    const circleHost = root.querySelector('[data-hanfi-float-circle] [data-hanfi-video-host]');
    const initialHost = root.dataset.floatState === 'minimized' ? circleHost : expandedHost;
    if (initialHost && root.dataset.floatState !== 'closed') {
      window.setTimeout(() => idle(() => loadVideo(initialHost, initialHost.dataset.autoplay === 'true')), initialDelay);
    }
    mobileQuery.addEventListener?.('change', applyResponsivePreviewDefault);

    root.addEventListener('click', (event) => {
      const minimize = event.target.closest('[data-hanfi-float-minimize]');
      const close = event.target.closest('[data-hanfi-float-close]');
      const restore = event.target.closest('[data-hanfi-float-restore]');
      const open = event.target.closest('[data-hanfi-float-open]');

      if (minimize) {
        event.preventDefault();
        previewStateTouched = true;
        root.dataset.floatState = 'minimized';
        pauseInactiveVideos(root, null);
        loadVideo(circleHost, true);
        return;
      }

      if (close) {
        event.preventDefault();
        previewStateTouched = true;
        root.dataset.floatState = 'closed';
        pauseInactiveVideos(root, null);
        if (persistClose) sessionStorage.setItem(storageKey, 'closed');
        return;
      }

      if (restore) {
        event.preventDefault();
        previewStateTouched = true;
        root.dataset.floatState = 'expanded';
        pauseInactiveVideos(root, null);
        loadVideo(expandedHost, expandedHost?.dataset.autoplay === 'true');
        return;
      }

      if (open) {
        event.preventDefault();
        previewStateTouched = true;
        openModal(root, root.dataset.activeIndex || open.dataset.videoIndex || 0);
      }
    });
  };

  const setupModal = (root) => {
    const dialog = root.querySelector('dialog[data-hanfi-video-modal]');
    if (!dialog) return;
    const panels = root.querySelectorAll('[data-hanfi-video-panel]');
    if (panels.length <= 1) {
      root.querySelectorAll('[data-hanfi-video-next-panel], [data-hanfi-video-prev-panel]').forEach((button) => {
        button.hidden = true;
      });
    }

    root.dataset.activeIndex = root.dataset.activeIndex || '0';
    root.querySelectorAll('[data-hanfi-video-close-modal]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeModal(root);
      });
    });
    root.querySelectorAll('[data-hanfi-video-next-panel]').forEach((button) => {
      button.addEventListener('click', () => activatePanel(root, Number(root.dataset.activeIndex || 0) + 1));
    });
    root.querySelectorAll('[data-hanfi-video-prev-panel]').forEach((button) => {
      button.addEventListener('click', () => activatePanel(root, Number(root.dataset.activeIndex || 0) - 1));
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeModal(root);
    });
    dialog.addEventListener('close', () => pauseInactiveVideos(root, null));
  };

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-hanfi-video-close-modal]');
    if (!closeButton) return;
    const root = closeButton.closest('[data-hanfi-shoppable-video-root], [data-hanfi-floating-video]');
    if (!root) return;
    event.preventDefault();
    event.stopPropagation();
    closeModal(root);
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-hanfi-video-audio-toggle]');
    if (!button) return;
    const scope = button.closest('[data-hanfi-float-expanded], [data-hanfi-float-circle], .hanfi-shoppable-card__stage, [data-hanfi-video-panel]');
    const host = scope?.querySelector('[data-hanfi-video-host]');
    if (!host) return;

    event.preventDefault();
    event.stopPropagation();
    loadVideo(host, true);

    const video = host.querySelector('video');
    if (!video) return;
    const shouldUnmute = video.muted;
    if (shouldUnmute) {
      unmuteVideo(video, button);
    } else {
      video.muted = true;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', button.closest('[data-hanfi-video-panel]') ? 'Unmute video' : 'Unmute preview');
    }

    video.play().catch(() => {});
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-hanfi-modal-cart-submit]');
    if (!button) return;
    const dialog = button.closest('dialog');
    const panel = dialog?.querySelector('[data-hanfi-video-panel]:not([hidden])');
    if (!panel) return;
    const form = panel.querySelector('[data-hanfi-add-form], .hanfi-shoppable-modal__form, .hanfi-floating-video__form');
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }, true);

  document.addEventListener('change', (event) => {
    const select = event.target.closest('.hanfi-shoppable-modal__form select, .hanfi-floating-video__form select');
    if (!select) return;
    const fallback = select.closest('[data-hanfi-pdp-fallback]');
    const buyNow = fallback?.querySelector('[data-hanfi-fallback-buy-now]');
    if (buyNow && select.value) buyNow.href = `/cart/${select.value}:1`;
  });

  const init = (root) => {
    if (!root || root.dataset.hanfiVideoReady === 'true') return;
    root.dataset.hanfiVideoReady = 'true';
    setupLazyHosts(root);
    setupModal(root);
    if (root.matches('[data-hanfi-floating-video]')) setupFloatingSection(root);
    else setupInlineSection(root);
  };

  const scheduleInit = (root) => {
    if (!root || root.dataset.hanfiVideoReady === 'true' || root.dataset.hanfiVideoQueued === 'true') return;
    root.dataset.hanfiVideoQueued = 'true';

    const runInit = () => init(root);
    root.addEventListener('pointerenter', runInit, { once: true, passive: true });
    root.addEventListener('pointerdown', runInit, { once: true, passive: true });
    root.addEventListener('focusin', runInit, { once: true });

    if (root.matches('[data-hanfi-floating-video]')) {
      idle(runInit);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      idle(runInit);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        idle(runInit);
      });
    }, { rootMargin: '320px 0px', threshold: 0.01 });

    observer.observe(root);
  };

  document.querySelectorAll('[data-hanfi-shoppable-video-root], [data-hanfi-floating-video]').forEach(scheduleInit);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-hanfi-shoppable-video-root], [data-hanfi-floating-video]').forEach(init);
  });
})();
